import React, { useEffect, useState, useContext, useRef } from 'react';
//@ts-ignore
import DateInput from '../DateInput/DateInput';
//@ts-ignore
import { ClientDateHandler } from '../ClientDateHandler/ClientDateHandler';
import {
  SelectOpt,
  ManualQuerySelections,
  VehicleInfoLookup,
  VehicleModelTrimLookup,
  VehicleState
} from '../../common/CommonTypes';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { PageAbsolutePath, VehicleLookup } from '../../../consts/Common';
import { VehicleApi } from '../../../api/VehicleApi';
import { getDateString } from '../../common/HelperMethods';
import './VehiclePage.scss';
import { SiteContext } from '../../../context/SiteContext';
import { ButtonConfirm } from '../Button/ButtonConfirm';
import { ButtonCommand } from '../Button/ButtonCommand';
import { CommonValidation } from '../CommonValidation/commonValidation';
import SelectInput, { SelectInputHelper } from '../SelectInput/SelectInput';
import { useTranslation } from 'react-i18next';
import { useCurrentPath } from '../../sitePages/useCurrentPath';
import { CountryIsoCodes } from '../Constants';

type initialQuerySelections = {
  firstRegistrationDate: string | undefined;
  firstRegistrationDateNew: string | undefined;
  vehicleTypeId: string;
  vehicleMakeId: string;
  vehicleModelId: string;
  vehicleFuelTypeId: string;
  vehicleEngineSizeId: string;
  vehicleModelTrimId: string;
  vehicleType: string;
  make: string;
  model: string;
  fuelType: string;
  engineSize: string;
};

export const initialManualQuerySelections: initialQuerySelections = {
  firstRegistrationDate: undefined,
  firstRegistrationDateNew: undefined,
  vehicleTypeId: '',
  vehicleMakeId: '',
  vehicleModelId: '',
  vehicleFuelTypeId: '',
  vehicleEngineSizeId: '',
  vehicleModelTrimId: '',
  vehicleType: '',
  make: '',
  model: '',
  fuelType: '',
  engineSize: ''
};

type VehicleDetailsManualProps = {
  onConfirmManualVehicleModal: (
    manualQuerySelections: ManualQuerySelections,
    enginePowerBHP?: number,
    enginePowerKWH?: number,
    driveTypeId?: number,
    vin?: string,
    modelType?: string,
    fuelTypeCode?: string
  ) => void;
  isFRDOptional?: boolean;
  forTest?: boolean;
  vehicle?: VehicleState;
  culture: string;
};

type VehicleType = {
  id: string;
  text: string;
  value: string;
  disabled: boolean;
};

const initialVehicleTypes: VehicleType[] = [
  { id: '1', text: 'Car', value: '1', disabled: false },
  { id: '2', text: 'Commercial Vehicle', value: '2', disabled: false },
  { id: '3', text: 'Motorcycle', value: '3', disabled: false },
  { id: '4', text: 'Motorhome', value: '4', disabled: false }
];

const VehicleDetailsManual = ({
  onConfirmManualVehicleModal,
  isFRDOptional,
  forTest,
  vehicle,
  culture
}: VehicleDetailsManualProps): JSX.Element => {
  const [vehicleTypes, setVehicleTypes] = useState<SelectOpt[]>(
    forTest ? initialVehicleTypes : []
  );
  const [vehicleMakes, setVehicleMakes] = useState<SelectOpt[]>([]);
  const [vehicleModels, setVehicleModels] = useState<SelectOpt[]>([]);
  const [vehicleFuelTypes, setVehicleFuelTypes] = useState<SelectOpt[]>([]);
  const [vehicleEngineSizes, setVehicleEngineSizes] = useState<SelectOpt[]>([]);
  const [vehicleModelTrims, setVehicleModelTrims] = useState<SelectOpt[]>([]);
  const [modelTrimLookupData, setModelTrimLookupData] = useState<
    VehicleModelTrimLookup[]
  >([]);

  const [loading, setLoading] = useState(false);

  const [isVehicleSetManually, setIsVehicleSetManually] = useState(false);

  const electricFuelTypeId = '4';

  const [manualQuerySelections, setManualQuerySelections] =
    useState<ManualQuerySelections>(initialManualQuerySelections);

  const [enableConfirmation, setEnableConfirmation] = useState(false);

  const { t } = useTranslation();

  const isElectric =
    manualQuerySelections.vehicleFuelTypeId === electricFuelTypeId;

  const showVehicleType =
    !CommonValidation.isDateNull(
      manualQuerySelections.firstRegistrationDate,
      true
    ) &&
    CommonValidation.isNotEmpty(manualQuerySelections.firstRegistrationDate);
  const showVehicleMake =
    !SelectInputHelper.isNullValue(manualQuerySelections.vehicleTypeId) &&
    !CommonValidation.isEmpty(manualQuerySelections.vehicleTypeId) &&
    vehicleMakes.length > 0;
  const showVehicleModel =
    showVehicleMake &&
    !SelectInputHelper.isNullValue(manualQuerySelections.vehicleMakeId) &&
    !CommonValidation.isEmpty(manualQuerySelections.vehicleMakeId);
  const showFuelType =
    showVehicleModel &&
    !SelectInputHelper.isNullValue(manualQuerySelections.vehicleModelId) &&
    !CommonValidation.isEmpty(manualQuerySelections.vehicleModelId);
  const showEngineSize =
    showFuelType &&
    !isElectric &&
    !SelectInputHelper.isNullValue(manualQuerySelections.vehicleFuelTypeId) &&
    !CommonValidation.isEmpty(manualQuerySelections.vehicleFuelTypeId);
  const showModelType =
    (showEngineSize || isElectric) &&
    !SelectInputHelper.isNullValue(manualQuerySelections.vehicleEngineSizeId) &&
    !CommonValidation.isEmpty(manualQuerySelections.vehicleEngineSizeId);

  const { site } = useContext(SiteContext);

  const nonGBSite =
    site.countryCode.toUpperCase() !== CountryIsoCodes.GB.toUpperCase();

  const isVehicleSet =
    vehicle && !CommonValidation.isEmpty(vehicle.vehicleDetails.make);

  const currentPath = useCurrentPath();

  const [showModal, setShowModal] = useState(
    nonGBSite &&
      !isVehicleSet &&
      currentPath === PageAbsolutePath.VehicleDetails
  );

  const showNoMakesError =
    !loading &&
    vehicleTypes.length > 0 &&
    vehicleMakes.length === 0 &&
    !CommonValidation.isEmpty(manualQuerySelections.vehicleTypeId) &&
    !SelectInputHelper.isNullValue(manualQuerySelections.vehicleTypeId);

  useEffect(() => {
    if (nonGBSite && isVehicleSet) {
      const existingManualQuerySelections: ManualQuerySelections = {
        firstRegistrationDate: vehicle.vehicleDeliveryDate,
        firstRegistrationDateNew: vehicle.vehicleDeliveryDate,
        vehicleTypeId: vehicle.vehicleDetails.vehicleTypeId,
        vehicleMakeId: vehicle.vehicleDetails.makeId,
        vehicleModelId: vehicle.vehicleDetails.modelId,
        vehicleFuelTypeId: vehicle.vehicleDetails.fuelTypeId.toString(),
        vehicleEngineSizeId: vehicle.vehicleDetails.engineSize,
        vehicleModelTrimId: vehicle.vehicleDetails.modelTrimId.toString(),
        vehicleType: vehicle.vehicleDetails.vehicleType,
        make: vehicle.vehicleDetails.make,
        model: vehicle.vehicleDetails.model,
        fuelType: vehicle.vehicleDetails.fuelType,
        engineSize: vehicle.vehicleDetails.engineSize
      };

      setManualQuerySelections(existingManualQuerySelections);
    }
  }, []);

  useEffect(() => {
    if (nonGBSite && isVehicleSet && !isVehicleSetManually && showModal) {
      reloadOptions(manualQuerySelections);

      setIsVehicleSetManually(true);
    }
  }, [showModal]);

  useEffect(() => {
    const enable = enableConfirmButton();

    if (enable != enableConfirmation) {
      setEnableConfirmation(enable);
    }
  }, [manualQuerySelections]);

  useEffect(() => {
    const controller = new AbortController();

    if (!vehicleEngineSizes.length && isElectric) {
      populateModelTrim('0', '0', controller.signal);
    }

    return () => {
      controller.abort();
    };
  }, [vehicleEngineSizes]);

  const reloadOptions = (selections: ManualQuerySelections) => {
    const abortController = new AbortController();

    getVehicleTypes(abortController.signal);

    getVehicleMakes(selections.vehicleTypeId, abortController.signal);

    getVehicleModels(selections.vehicleMakeId, abortController.signal);

    getFuelTypes(selections.vehicleModelId, abortController.signal);

    getEngineSizes(selections.vehicleFuelTypeId, abortController.signal);

    getModelTrims(selections.vehicleEngineSizeId, abortController.signal);

    return function cancel() {
      abortController.abort();
    };
  };

  const enableConfirmButton = () => {
    const enable =
      (isFRDOptional ||
        (!CommonValidation.isDateNull(
          manualQuerySelections.firstRegistrationDate,
          true
        ) &&
          CommonValidation.isNotEmpty(
            manualQuerySelections.firstRegistrationDate
          ))) &&
      !SelectInputHelper.isNullValue(manualQuerySelections.vehicleTypeId) &&
      CommonValidation.isNotEmpty(manualQuerySelections.vehicleTypeId) &&
      !SelectInputHelper.isNullValue(manualQuerySelections.vehicleMakeId) &&
      CommonValidation.isNotEmpty(manualQuerySelections.vehicleMakeId) &&
      !SelectInputHelper.isNullValue(manualQuerySelections.vehicleModelId) &&
      CommonValidation.isNotEmpty(manualQuerySelections.vehicleModelId) &&
      !SelectInputHelper.isNullValue(manualQuerySelections.vehicleFuelTypeId) &&
      CommonValidation.isNotEmpty(manualQuerySelections.vehicleFuelTypeId) &&
      !SelectInputHelper.isNullValue(
        manualQuerySelections.vehicleEngineSizeId
      ) &&
      CommonValidation.isNotEmpty(manualQuerySelections.vehicleEngineSizeId) &&
      !SelectInputHelper.isNullValue(
        manualQuerySelections.vehicleModelTrimId
      ) &&
      CommonValidation.isNotEmpty(manualQuerySelections.vehicleModelTrimId);

    return enable;
  };

  const onShowModal = () => {
    setShowModal(true);
  };

  const onCloseModal = () => {
    if (!isVehicleSet) {
      setManualQuerySelections(initialManualQuerySelections);
    }

    setShowModal(false);
  };

  const onConfirm = () => {
    const vehicleModelTrim = modelTrimLookupData.find(
      (m) => m.id == manualQuerySelections.vehicleModelTrimId
    );

    onConfirmManualVehicleModal(
      manualQuerySelections,
      vehicleModelTrim?.enginePowerBHP,
      vehicleModelTrim?.enginePowerKWH,
      vehicleModelTrim?.driveTypeId,
      vehicleModelTrim?.vin ?? '',
      vehicleModelTrim?.text,
      vehicleModelTrim?.fuelTypeCode
    );

    setIsVehicleSetManually(true);

    setShowModal(false);
  };

  const onDateCalendarClose = () => {
    const abortController = new AbortController();

    if (
      manualQuerySelections.firstRegistrationDateNew !=
      manualQuerySelections.firstRegistrationDate
    ) {
      const newManualQuerySelections = {
        ...manualQuerySelections,
        ['firstRegistrationDate']:
          manualQuerySelections.firstRegistrationDateNew,
        ['vehicleTypeId']: SelectInputHelper.NullValue(),
        ['vehicleMakeId']: SelectInputHelper.NullValue(),
        ['vehicleModelId']: SelectInputHelper.NullValue(),
        ['vehicleFuelTypeId']: SelectInputHelper.NullValue(),
        ['vehicleEngineSizeId']: SelectInputHelper.NullValue(),
        ['vehicleModelTrimId']: SelectInputHelper.NullValue()
      };

      setManualQuerySelections(newManualQuerySelections);

      if (
        !CommonValidation.isDateNull(
          manualQuerySelections.firstRegistrationDateNew,
          true
        ) &&
        CommonValidation.isNotEmpty(
          manualQuerySelections.firstRegistrationDateNew
        )
      ) {
        getVehicleTypes(abortController.signal);
      }
    }

    return function cancel() {
      abortController.abort();
    };
  };

  const getVehicleTypes = (signal: AbortSignal) => {
    new VehicleApi().getVehicleTypes(
      site.countryCode,
      getRegistrationDate(),
      site.user.token,
      successCallbackVehicleTypes,
      failureCallbackVehicleInfo,
      signal
    );
  };

  const getVehicleMakes = (value: string, signal: AbortSignal) => {
    setLoading(true);
    new VehicleApi().getVehicleMakes(
      getRegistrationDate(),
      value,
      site.user.token,
      successCallbackVehicleMakes,
      failureCallbackVehicleInfo,
      signal
    );
  };

  const getVehicleModels = (value: string, signal: AbortSignal) => {
    new VehicleApi().getVehicleModels(
      getRegistrationDate(),
      manualQuerySelections.vehicleTypeId,
      value,
      site.user.token,
      successCallbackVehicleModels,
      failureCallbackVehicleInfo,
      signal
    );
  };

  const getFuelTypes = (value: string, signal: AbortSignal) => {
    new VehicleApi().getFuelTypes(
      site.countryCode,
      getRegistrationDate(),
      manualQuerySelections.vehicleTypeId,
      manualQuerySelections.vehicleMakeId,
      value,
      site.user.token,
      successCallbackFuelTypes,
      failureCallbackVehicleInfo,
      signal
    );
  };

  const getEngineSizes = (value: string, signal: AbortSignal) => {
    new VehicleApi().getEngineSizes(
      getRegistrationDate(),
      manualQuerySelections.vehicleTypeId,
      manualQuerySelections.vehicleMakeId,
      manualQuerySelections.vehicleModelId,
      value,
      site.user.token,
      successCallbackEngineSizes,
      failureCallbackVehicleInfo,
      signal
    );
  };

  const getModelTrims = (value: string, signal: AbortSignal) => {
    new VehicleApi().getModelTrims(
      getRegistrationDate(),
      manualQuerySelections.vehicleTypeId,
      manualQuerySelections.vehicleMakeId,
      manualQuerySelections.vehicleModelId,
      manualQuerySelections.vehicleFuelTypeId,
      value,
      site.user.token,
      successCallbackModelTrims,
      failureCallbackVehicleInfo,
      signal
    );
  };

  const populateModelTrim = (
    engineSizeId: string,
    engineSize: string,
    abortSignal: AbortSignal
  ) => {
    if (
      !SelectInputHelper.isNullValue(engineSizeId) &&
      engineSizeId != manualQuerySelections.vehicleEngineSizeId
    ) {
      getModelTrims(engineSizeId, abortSignal);
    }

    const newManualQuerySelections = {
      ...manualQuerySelections,
      ['engineSize']: engineSize,
      ['vehicleEngineSizeId']: engineSizeId,
      ['vehicleModelTrimId']: SelectInputHelper.NullValue()
    };
    setManualQuerySelections(newManualQuerySelections);
  };

  const onChangeDetailWithLookup = (
    selectedValue: string,
    lookupValue: number,
    options?: SelectOpt[]
  ) => {
    let text = '';
    if (options) {
      text =
        options.find((o) => o.value.toString() === selectedValue)?.text ?? '';
    }
    const abortController = new AbortController();

    switch (lookupValue) {
      case VehicleLookup.Types:
        {
          const newManualQuerySelections = {
            ...manualQuerySelections,
            ['firstRegistrationDateNew']: selectedValue
          };

          setManualQuerySelections(newManualQuerySelections);
        }
        break;
      case VehicleLookup.Makes:
        {
          if (
            !SelectInputHelper.isNullValue(selectedValue) &&
            selectedValue != manualQuerySelections.vehicleTypeId
          ) {
            getVehicleMakes(selectedValue, abortController.signal);
          }

          const newManualQuerySelections = {
            ...manualQuerySelections,
            ['vehicleType']: text,
            ['vehicleTypeId']: selectedValue,
            ['vehicleMakeId']: SelectInputHelper.NullValue(),
            ['vehicleModelId']: SelectInputHelper.NullValue(),
            ['vehicleFuelTypeId']: SelectInputHelper.NullValue(),
            ['vehicleEngineSizeId']: SelectInputHelper.NullValue(),
            ['vehicleModelTrimId']: SelectInputHelper.NullValue()
          };
          setManualQuerySelections(newManualQuerySelections);
        }
        break;
      case VehicleLookup.Model:
        {
          if (
            !SelectInputHelper.isNullValue(selectedValue) &&
            selectedValue != manualQuerySelections.vehicleMakeId
          ) {
            getVehicleModels(selectedValue, abortController.signal);
          }
          const newManualQuerySelections = {
            ...manualQuerySelections,
            ['make']: text,
            ['vehicleMakeId']: selectedValue,
            ['vehicleModelId']: SelectInputHelper.NullValue(),
            ['vehicleFuelTypeId']: SelectInputHelper.NullValue(),
            ['vehicleEngineSizeId']: SelectInputHelper.NullValue(),
            ['vehicleModelTrimId']: SelectInputHelper.NullValue()
          };
          setManualQuerySelections(newManualQuerySelections);
        }
        break;
      case VehicleLookup.Fueltype:
        {
          if (
            !SelectInputHelper.isNullValue(selectedValue) &&
            selectedValue != manualQuerySelections.vehicleModelId
          ) {
            getFuelTypes(selectedValue, abortController.signal);
          }
          const newManualQuerySelections = {
            ...manualQuerySelections,
            ['model']: text,
            ['vehicleModelId']: selectedValue,
            ['vehicleFuelTypeId']: SelectInputHelper.NullValue(),
            ['vehicleEngineSizeId']: SelectInputHelper.NullValue(),
            ['vehicleModelTrimId']: SelectInputHelper.NullValue()
          };
          setManualQuerySelections(newManualQuerySelections);
        }
        break;
      case VehicleLookup.Enginesize:
        {
          if (
            !SelectInputHelper.isNullValue(selectedValue) &&
            selectedValue != manualQuerySelections.vehicleFuelTypeId
          ) {
            if (selectedValue === electricFuelTypeId) {
              setVehicleEngineSizes([]);
            } else {
              getEngineSizes(selectedValue, abortController.signal);
            }
          }

          const newManualQuerySelections = {
            ...manualQuerySelections,
            ['fuelType']: text,
            ['vehicleFuelTypeId']: selectedValue,
            ['vehicleEngineSizeId']: SelectInputHelper.NullValue(),
            ['vehicleModelTrimId']: SelectInputHelper.NullValue()
          };
          setManualQuerySelections(newManualQuerySelections);
        }
        break;
      case VehicleLookup.Modeltrim:
        populateModelTrim(selectedValue, text, abortController.signal);
        break;
      case VehicleLookup.Finished:
        {
          const newManualQuerySelections = {
            ...manualQuerySelections,
            ['vehicleModelTrimId']: selectedValue
          };
          setManualQuerySelections(newManualQuerySelections);
        }
        break;
      default:
        break;
    }

    return () => {
      abortController.abort();
    };
  };

  const successCallbackVehicleTypes = (data: VehicleInfoLookup[]) => {
    const mappedVehicleTypes = mapVehicleSection(data);
    setVehicleTypes(mappedVehicleTypes);
  };

  const successCallbackVehicleMakes = (data: VehicleInfoLookup[]) => {
    const mappedVehicleMakes = mapVehicleSection(
      site.brand.brandName === 'landrover'
        ? data.filter((d) => d.text === 'Land Rover')
        : data
    );
    setLoading(false);
    setVehicleMakes(mappedVehicleMakes);
  };

  const successCallbackVehicleModels = (data: VehicleInfoLookup[]) => {
    const mappedVehicleModels = mapVehicleSection(data);
    setVehicleModels(mappedVehicleModels);
  };

  const successCallbackFuelTypes = (data: VehicleInfoLookup[]) => {
    const mappedFuelTypes = mapVehicleSection(data);
    setVehicleFuelTypes(mappedFuelTypes);
  };

  const successCallbackEngineSizes = (data: VehicleInfoLookup[]) => {
    const mappedEngineSizes = mapVehicleSection(data);
    setVehicleEngineSizes(mappedEngineSizes);
  };

  const successCallbackModelTrims = (data: VehicleModelTrimLookup[]) => {
    setModelTrimLookupData(data);
    const mappedModelTrims = mapVehicleSection(data);

    setVehicleModelTrims(mappedModelTrims);
  };

  const failureCallbackVehicleInfo = (error: string) => {
    console.log('Failed to get with: ', error);
    setLoading(false);
  };

  const mapVehicleSection = (
    vehicleInformation: VehicleInfoLookup[]
  ): SelectOpt[] => {
    const mappedVehicleOptions: SelectOpt[] = [];
    vehicleInformation.forEach((info) => {
      const option = {
        id: info.id,
        text: info.text,
        value: info.value,
        disabled: false
      };
      mappedVehicleOptions.push(option);
    });
    return mappedVehicleOptions;
  };

  const activeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showModal && activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [
    showVehicleType,
    showVehicleMake,
    showVehicleModel,
    showFuelType,
    showEngineSize,
    showModelType
  ]);

  const getRegistrationDate = (): string => {
    if (
      !CommonValidation.isDateNull(
        manualQuerySelections.firstRegistrationDateNew,
        true
      ) &&
      CommonValidation.isNotEmpty(
        manualQuerySelections.firstRegistrationDateNew
      )
    ) {
      return getDateString(manualQuerySelections.firstRegistrationDateNew);
    } else if (isFRDOptional === true) {
      const cdh = new ClientDateHandler();

      cdh.setDateValue(false, new Date());

      const todayDateJson = cdh.getJSON();
      return getDateString(todayDateJson);
    } else if (
      vehicle &&
      !CommonValidation.isDateNull(vehicle.vehicleDeliveryDate, true)
    ) {
      return getDateString(vehicle.vehicleDeliveryDate);
    } else {
      return '';
    }
  };

  useEffect(() => {
    if (isFRDOptional === true) {
      const abortController = new AbortController();

      new VehicleApi().getVehicleTypes(
        site.countryCode,
        getRegistrationDate(),
        site.user.token,
        successCallbackVehicleTypes,
        failureCallbackVehicleInfo,
        abortController.signal
      );
    }
  }, [isFRDOptional]);

  const getFRDFieldLabel = () => {
    let label = t(
      'vehicle-details.manual-vehicle-details.vehicle-registration-date-label'
    );

    if (isFRDOptional) {
      label += ` ${t('vehicle-details.optional-field-text')}`;
    }

    return label;
  };

  const labelText = nonGBSite ? '' : t('vehicle-details.show-modal-button');

  const buttonText = nonGBSite
    ? t('vehicle-details.edit-vehicle-details-button')
    : t('vehicle-details.enter-details-manually-button');

  return (
    <>
      <div className='flex-row m2-top m1-bottom'>
        <p>
          {!isFRDOptional && <>{labelText}</>}

          <ButtonCommand
            name='btn-manual-vehicle'
            text={buttonText}
            onClick={onShowModal}
            additionalClassNames='bold'
          />
        </p>
      </div>

      <Modal isOpen={showModal}>
        <ModalHeader
          toggle={onCloseModal}
          className='modal-header-title cms-modal-header-title'>
          {t(
            'vehicle-details.manual-vehicle-details.manual-vehicle-details-header'
          )}
          <span className='close' aria-hidden='true' onClick={onCloseModal}>
            ×
          </span>
        </ModalHeader>
        <ModalBody>
          <>
            <div className='flex-row'>
              <div className='control'>
                <DateInput
                  name='first-registration-date'
                  className='input-text w250'
                  haslabel
                  label={getFRDFieldLabel()}
                  placeholder={t(
                    'vehicle-details.manual-vehicle-details.vehicle-registration-date-placeholder'
                  )}
                  forceTimeReset
                  useJson
                  error=''
                  value={manualQuerySelections.firstRegistrationDate}
                  onDateChanged={(e: React.FormEvent<HTMLInputElement>) => {
                    onChangeDetailWithLookup(
                      (e.target as HTMLSelectElement).value,
                      VehicleLookup.Types
                    );
                  }}
                  onCalendarClose={onDateCalendarClose}
                  culture={culture}
                />
              </div>
            </div>
            <div className='flex-row'>
              {(isFRDOptional || showVehicleType) && (
                <div className='inline-control'>
                  <div className='control-label'>
                    {t(
                      'vehicle-details.manual-vehicle-details.vehicle-type-label'
                    )}
                  </div>
                  <div className='control'>
                    <SelectInput
                      name='vehicle_type'
                      className='input-text w250'
                      value={manualQuerySelections.vehicleTypeId}
                      options={vehicleTypes}
                      onChange={(selectedValue: string) => {
                        onChangeDetailWithLookup(
                          selectedValue,
                          VehicleLookup.Makes,
                          vehicleTypes
                        );
                      }}
                      defaultText={t(
                        'vehicle-details.manual-vehicle-details.vehicle-type-placeholder'
                      )}
                      forceDefault
                      addDefaultOption
                      error=''
                      loadingText={t('loading-placeholder.loading-text')}
                    />
                  </div>
                </div>
              )}
              <div id='vehicle-id-scroll' ref={activeRef} />
              {showVehicleMake && (
                <div className='inline-control'>
                  <div className='control-label'>
                    {t(
                      'vehicle-details.manual-vehicle-details.vehicle-make-label'
                    )}
                  </div>
                  <div className='control'>
                    <SelectInput
                      name='vehicle_make'
                      className='input-text w250'
                      value={manualQuerySelections.vehicleMakeId}
                      options={vehicleMakes}
                      onChange={(selectedValue: string) => {
                        onChangeDetailWithLookup(
                          selectedValue,
                          VehicleLookup.Model,
                          vehicleMakes
                        );
                      }}
                      defaultText={t(
                        'vehicle-details.manual-vehicle-details.vehicle-make-placeholder'
                      )}
                      forceDefault
                      addDefaultOption
                      error=''
                      loadingText={t('loading-placeholder.loading-text')}
                    />
                  </div>
                </div>
              )}
              {showNoMakesError && (
                <div className='flex-row'>
                  <div className='control-label'>
                    {t('vehicle-details.manual-vehicle-details.no-makes-found')}
                  </div>
                </div>
              )}
              <div id='vehicle-make-scroll' ref={activeRef} />
            </div>
            <div className='flex-row'>
              {showVehicleModel && (
                <div className='inline-control'>
                  <div className='control-label'>
                    {t(
                      'vehicle-details.manual-vehicle-details.vehicle-model-label'
                    )}
                  </div>
                  <div className='control'>
                    <SelectInput
                      name='vehicle-model'
                      className='input-text w250'
                      value={manualQuerySelections.vehicleModelId}
                      options={vehicleModels}
                      onChange={(selectedValue: string) => {
                        onChangeDetailWithLookup(
                          selectedValue,
                          VehicleLookup.Fueltype,
                          vehicleModels
                        );
                      }}
                      defaultText={t(
                        'vehicle-details.manual-vehicle-details.vehicle-model-placeholder'
                      )}
                      forceDefault
                      addDefaultOption
                      error=''
                      loadingText={t('loading-placeholder.loading-text')}
                    />
                  </div>
                </div>
              )}
              <div id='vehicle-model-scroll' ref={activeRef} />
              {showFuelType && (
                <div className='inline-control'>
                  <div className='control-label'>
                    {t('vehicle-details.fuel-type-label')}
                  </div>
                  <div className='control' ref={activeRef}>
                    <SelectInput
                      name='fuel-type'
                      className='input-text w250'
                      value={manualQuerySelections.vehicleFuelTypeId}
                      options={vehicleFuelTypes}
                      onChange={(selectedValue: string) => {
                        onChangeDetailWithLookup(
                          selectedValue,
                          VehicleLookup.Enginesize,
                          vehicleFuelTypes
                        );
                      }}
                      defaultText={t(
                        'vehicle-details.manual-vehicle-details.fuel-type-placeholder'
                      )}
                      forceDefault
                      addDefaultOption
                      error=''
                      loadingText={t('loading-placeholder.loading-text')}
                    />
                  </div>
                </div>
              )}
              <div id='fuel-type-scroll' ref={activeRef} />
            </div>
            <div className='flex-row'>
              {showEngineSize && (
                <div className='inline-control'>
                  <div className='control-label'>
                    {t(
                      'vehicle-details.manual-vehicle-details.engine-size-label'
                    )}
                  </div>
                  <div className='control'>
                    <SelectInput
                      name='engine-size'
                      className='input-text w250'
                      value={manualQuerySelections.vehicleEngineSizeId}
                      options={vehicleEngineSizes}
                      onChange={(selectedValue: string) => {
                        onChangeDetailWithLookup(
                          selectedValue,
                          VehicleLookup.Modeltrim,
                          vehicleEngineSizes
                        );
                      }}
                      defaultText={t(
                        'vehicle-details.manual-vehicle-details.engine-size-placeholder'
                      )}
                      forceDefault
                      addDefaultOption
                      error=''
                      loadingText={t('loading-placeholder.loading-text')}
                    />
                  </div>
                </div>
              )}
              <div id='engine-size-scroll' ref={activeRef} />
              {showModelType && (
                <div className='inline-control'>
                  <div className='control-label'>
                    {t('vehicle-details.model-type-label')}
                  </div>
                  <div className='control'>
                    <SelectInput
                      name='model-type'
                      className='input-text w250'
                      value={manualQuerySelections.vehicleModelTrimId}
                      options={vehicleModelTrims}
                      onChange={(selectedValue: string) => {
                        onChangeDetailWithLookup(
                          selectedValue,
                          VehicleLookup.Finished,
                          vehicleModelTrims
                        );
                      }}
                      defaultText={t(
                        'vehicle-details.manual-vehicle-details.model-type-placeholder'
                      )}
                      forceDefault
                      addDefaultOption
                      error=''
                      loadingText={t('loading-placeholder.loading-text')}
                    />
                  </div>
                </div>
              )}
              <div id='model-type-scroll' ref={activeRef} />
            </div>
          </>
        </ModalBody>
        <ModalFooter>
          <div className='flex-row button-row'>
            <ButtonCommand
              name='btn-close-manual-vehicle'
              text={t('vehicle-details.button-cancel-text')}
              onClick={onCloseModal}
            />

            <ButtonConfirm
              name='btn-confirm-manual-vehicle'
              text={t('vehicle-details.button-confirm-text')}
              additionalClassNames='m05-left'
              onClick={onConfirm}
              disabled={!enableConfirmation}
            />
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default VehicleDetailsManual;
